import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from 'react-toastify';
import { useUser } from '../context/UserContext';
import { useToastFromNavigation } from '../hooks/useToastFromNavigation';
import { Mail, Lock, Eye, EyeOff, UserRound, Building2, Phone } from 'lucide-react';

export default function AuthForm() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    business_name: "",
    phone: ""
  });

  const { setUser } = useUser();
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // ✅ Κλήση του hook εδώ
  useToastFromNavigation();

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setFormData({ name: "", email: "", password: "", business_name: "", phone: "" });
    setShowPassword(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = isLogin
      ? {
          email: formData.email,
          password: formData.password,
          rememberMe: rememberMe,
        }
      : formData;

    const API = process.env.REACT_APP_API_BASE_URL || 'https://python.remindly.gr';

    const url = isLogin
      ? `${API}/auth/login`
      : `${API}/auth/register`;

    try {
      const res = await axios.post(url, payload);
      console.log("✅ Success:", res.data);

      if (isLogin && res.data.success) {
        localStorage.setItem("token", res.data.token); // ──▶ ADD THIS LINE
        setUser(res.data.user);

        toast.success("Επιτυχής σύνδεση! Μεταφορά στο ημερολόγιο...", {
          autoClose: 2500,
        });

        setTimeout(() => {
          navigate("/calendar");
        }, 2500);
      }
      else {
        toast.success("🎉 Εγγραφή επιτυχής!");
      }
    } catch (err) {
      console.error("Login error:", err.response?.data);
      toast.error("Σφάλμα: " + (err.response?.data?.error || "Κάτι πήγε στραβά."));
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-white text-slate-900"
      style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif" }}
    >
      {/* Left branding panel */}
      <aside
        className="hidden lg:flex relative overflow-hidden text-white flex-col justify-between p-12 xl:p-16"
        style={{
          background:
            "radial-gradient(circle at 15% 15%, rgba(6,182,212,.20), transparent 32%)," +
            "radial-gradient(circle at 85% 25%, rgba(124,58,237,.24), transparent 34%)," +
            "radial-gradient(circle at 30% 90%, rgba(79,70,229,.24), transparent 38%)," +
            "linear-gradient(180deg, #0d1326 0%, #111933 100%)",
        }}
      >
        <Link to="/" className="flex items-center gap-3 font-extrabold text-lg relative z-10">
          <span className="w-11 h-11 rounded-xl grid place-items-center font-black text-white bg-gradient-to-br from-indigo-600 to-violet-600 shadow-lg shadow-indigo-900/40">R</span>
          <span>Remindly</span>
        </Link>

        <div className="relative z-10 max-w-md">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white/90 font-semibold text-sm mb-6">
            Καλώς ήρθες πίσω
          </span>
          <h1 className="text-4xl xl:text-[2.75rem] font-extrabold leading-tight tracking-tight mb-4">
            Το πρόγραμμά σου,<br className="hidden xl:block" /> πάντα υπό έλεγχο.
          </h1>
          <p className="text-white/70 text-base leading-relaxed mb-8">
            Συνδέσου στο Remindly για να διαχειριστείς τα ραντεβού, την ομάδα σου και τις υπενθυμίσεις πελατών — όλα από ένα μέρος.
          </p>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <strong className="block text-white text-lg">−32%</strong>
              <span className="text-white/60 text-xs">Χαμένα Ραντεβού</span>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <strong className="block text-white text-lg">1</strong>
              <span className="text-white/60 text-xs">Κεντρικό ημερολόγιο</span>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <strong className="block text-white text-lg">24/7</strong>
              <span className="text-white/60 text-xs">Πρόσβαση από παντού</span>
            </div>
          </div>
        </div>

        <p className="relative z-10 text-white/40 text-sm">© 2026 Remindly. Όλα τα δικαιώματα διατηρούνται.</p>

        {/* decorative blobs */}
        <div className="pointer-events-none absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-violet-600/20 blur-3xl"></div>
        <div className="pointer-events-none absolute top-10 -left-10 w-56 h-56 rounded-full bg-cyan-500/20 blur-3xl"></div>
      </aside>

      {/* Right form panel */}
      <main className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">

          {/* mobile logo */}
          <Link to="/" className="lg:hidden flex w-full items-center justify-center gap-3 font-extrabold text-lg mb-10">
            <span className="w-11 h-11 rounded-xl grid place-items-center font-black text-white bg-gradient-to-br from-indigo-600 to-violet-600 shadow-lg shadow-indigo-900/40">R</span>
            <span className="text-slate-900">Remindly</span>
          </Link>

          <h2 className="text-2xl sm:text-[1.75rem] font-extrabold tracking-tight mb-2">
            {isLogin ? "Σύνδεση στον λογαριασμό" : "Δημιουργία λογαριασμού"}
          </h2>
          <p className="text-slate-500 mb-8">
            {isLogin ? "Εισάγετε το email και τον κωδικό πρόσβασης για να συνδεθείτε." : "Ξεκίνα με το Remindly σε λίγα λεπτά."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">Ονοματεπώνυμο</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                      <UserRound className="w-5 h-5" />
                    </span>
                    <input
                      id="name" name="name" type="text" placeholder="Το όνομά σου"
                      value={formData.name} onChange={handleChange} required
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-11 pr-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 outline-none transition"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="business_name" className="block text-sm font-semibold text-slate-700 mb-1.5">Όνομα επιχείρησης</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                      <Building2 className="w-5 h-5" />
                    </span>
                    <input
                      id="business_name" name="business_name" type="text" placeholder="π.χ. Κομμωτήριο Άννα"
                      value={formData.business_name} onChange={handleChange} required
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-11 pr-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 outline-none transition"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1.5">Τηλέφωνο</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                      <Phone className="w-5 h-5" />
                    </span>
                    <input
                      id="phone" name="phone" type="text" placeholder="69XXXXXXXX"
                      value={formData.phone} onChange={handleChange}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-11 pr-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 outline-none transition"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                  <Mail className="w-5 h-5" />
                </span>
                <input
                  id="email" name="email" type="email" autoComplete="email" placeholder="name@business.gr"
                  value={formData.email} onChange={handleChange} required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-11 pr-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 outline-none transition"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-semibold text-slate-700">Κωδικός</label>
                {isLogin && (
                  <Link to="/forgot-password" className="text-sm font-semibold text-indigo-600 hover:text-violet-600 transition">
                    Ξέχασες τον κωδικό;
                  </Link>
                )}
              </div>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                  <Lock className="w-5 h-5" />
                </span>
                <input
                  id="password" name="password" type={showPassword ? "text" : "password"}
                  autoComplete={isLogin ? "current-password" : "new-password"} placeholder="••••••••"
                  value={formData.password} onChange={handleChange} required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-11 pr-11 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 outline-none transition"
                />
                <button
                  type="button" onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? "Απόκρυψη κωδικού" : "Εμφάνιση κωδικού"}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox" checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-100"
                  />
                  <span className="text-sm text-slate-600">Να με θυμάσαι</span>
                </label>
              </div>
            )}

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center rounded-xl py-3.5 font-bold text-white bg-gradient-to-br from-indigo-600 to-violet-600 shadow-lg shadow-indigo-500/30 hover:-translate-y-0.5 hover:shadow-xl transition"
            >
              {isLogin ? "Σύνδεση" : "Δημιουργία Λογαριασμού"}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-8">
            {isLogin ? (
              <>
                Δεν έχεις λογαριασμό;{" "}
                <button type="button" onClick={toggleForm} className="font-semibold text-indigo-600 hover:text-violet-600 transition">
                  Εγγραφή
                </button>
              </>
            ) : (
              <>
                Έχεις ήδη λογαριασμό;{" "}
                <button type="button" onClick={toggleForm} className="font-semibold text-indigo-600 hover:text-violet-600 transition">
                  Σύνδεση
                </button>
              </>
            )}
          </p>
        </div>
      </main>
    </div>
  );
}
